/**
 * Assinatura em `users/{uid}/subscriptions/current`, com merge de campos legados em `users/{uid}`.
 */

import type { Firestore } from 'firebase/firestore';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getFirestoreDb } from '@/core/services';
import {
  mergeSubscriptionRaw,
  normalizeSubscriptionFields,
  SUBSCRIPTION_DOC_ID,
  SubscriptionRef,
  subscriptionSchema,
  UserRef,
  type Subscription,
} from '@/core/domain';

export interface SubscriptionRemoteDataSource {
  getByUserId(userId: string): Promise<Subscription | null>;
  getByUserIds(userIds: string[]): Promise<Map<string, Subscription | null>>;
  /**
   * Escuta user + doc `subscriptions/current` e emite assinatura mesclada após cada mudança.
   */
  subscribeMerged(
    userId: string,
    onNext: (subscription: Subscription | null) => void,
    onError?: (error: Error) => void
  ): () => void;
}

function toSubscription(
  userId: string,
  subscriptionDoc: Record<string, unknown> | undefined,
  userDoc: Record<string, unknown> | undefined
): Subscription | null {
  const merged = mergeSubscriptionRaw(userId, subscriptionDoc, userDoc);
  const normalized = normalizeSubscriptionFields(userId, merged);
  const parsed = subscriptionSchema.safeParse(normalized);
  if (!parsed.success) return null;
  return parsed.data;
}

export class SubscriptionFirestoreDataSource implements SubscriptionRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  private subscriptionDocRef(userId: string) {
    return doc(
      this.getDb(),
      UserRef.collection,
      userId,
      SubscriptionRef.collection,
      SUBSCRIPTION_DOC_ID
    );
  }

  async getByUserId(userId: string): Promise<Subscription | null> {
    const db = this.getDb();
    const userRef = doc(db, UserRef.collection, userId);
    const subRef = this.subscriptionDocRef(userId);
    const [userSnap, subSnap] = await Promise.all([getDoc(userRef), getDoc(subRef)]);
    const userData = userSnap.exists() ? (userSnap.data() as Record<string, unknown>) : undefined;
    const subData = subSnap.exists() ? (subSnap.data() as Record<string, unknown>) : undefined;
    return toSubscription(userId, subData, userData);
  }

  async getByUserIds(userIds: string[]): Promise<Map<string, Subscription | null>> {
    const map = new Map<string, Subscription | null>();
    await Promise.all(
      userIds.map(async (uid) => {
        const s = await this.getByUserId(uid);
        map.set(uid, s);
      })
    );
    return map;
  }

  subscribeMerged(
    userId: string,
    onNext: (subscription: Subscription | null) => void,
    onError?: (error: Error) => void
  ): () => void {
    const db = this.getDb();
    const userRef = doc(db, UserRef.collection, userId);
    const subRef = this.subscriptionDocRef(userId);

    let latestUser: Record<string, unknown> | undefined;
    let latestSub: Record<string, unknown> | undefined;
    let userReady = false;
    let subReady = false;

    const emit = (): void => {
      if (!userReady || !subReady) return;
      onNext(toSubscription(userId, latestSub, latestUser));
    };

    const unsubUser = onSnapshot(
      userRef,
      (snap) => {
        latestUser = snap.exists() ? (snap.data() as Record<string, unknown>) : undefined;
        userReady = true;
        emit();
      },
      (err) => {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    );

    const unsubSub = onSnapshot(
      subRef,
      (snap) => {
        latestSub = snap.exists() ? (snap.data() as Record<string, unknown>) : undefined;
        subReady = true;
        emit();
      },
      (err) => {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    );

    return () => {
      unsubUser();
      unsubSub();
    };
  }
}
